import axios from "axios";
import { AUTH_TOKEN, DOMAIN_ID, REACT_APP_API } from "../constants";

// AuthNull as the identity provider for a tenant's own applications (OpenID
// Connect, SAML 2.0, MFA API). Backend: authnull-service internal/idp —
// routes.go RegisterAdminRoutes mounts these on /api/v1/idp, behind a session
// in the organisation and the administrator role; handlers in admin.go.
//
// Every call is POST JSON with X-Authorization. Each helper resolves to the
// response body and rejects with the axios error (or an Error with .status for
// a non-JSON 2xx). Statuses:
//   200 ok
//   400 { error } — the administrator's to fix: show it as is, next to the form
//   401 session expired: log out
//   403 { error: "administrator role required" }
//   404 { error: "no such application" | "no such login source" }: refresh
//   500 { error: "could not …" }: generic error toast
// idpFailure(err) sorts an error into those.
const BASE = "/api/v1/idp";

const post = (path, payload = {}) =>
  axios
    .post(`${REACT_APP_API}${BASE}/${path}`, payload, {
      withCredentials: true,
      headers: { "X-Authorization": AUTH_TOKEN() },
    })
    .then((res) => {
      // An unrouted path falls through to the SPA catch-all: 200 text/html.
      if (typeof res?.data !== "object" || res.data === null) {
        const e = new Error("The server returned an unexpected response.");
        e.status = res?.status;
        throw e;
      }
      return res.data;
    });

const tenantId = () => Number(DOMAIN_ID()) || 0;

// → { apps: [App] }
// App: { id, tenantId, name, protocol: "oidc"|"saml"|"mfa_api", enabled, logoUrl, mfaMode,
//        oidc?: { clientId, tokenEndpointAuthMethod, redirectUris, postLogoutRedirectUris,
//                 allowedScopes, requirePkce, hasSecret },
//        saml?: { entityId, acsUrls, nameIdFormat, nameIdSource, attributes }, createdAt, updatedAt }
export const listApps = () => post("apps/list", { tenantId: tenantId() });

// body: { id?, name, protocol, logoUrl, disabled, oidc?: {…}, saml?: {…} } (tenantId added)
// → { app, clientSecret? } — clientSecret only when one was just made.
export const saveApp = (body) => post("apps/save", { ...body, tenantId: tenantId() });

// → { status: "deleted" }
export const deleteApp = (id) => post("apps/delete", { id });

// → { clientSecret } (400 for a public client: it has no secret)
export const rotateAppSecret = (id) => post("apps/rotateSecret", { id });

// → { assignments: [{ type: "user"|"group", principal, label? }] }
export const listAppAssignments = (id) => post("apps/assignments", { id });

// Replaces every assignment (max 500). → { assignments }
export const saveAppAssignments = (id, assignments) =>
  post("apps/saveAssignments", {
    id,
    assignments: (assignments || []).map(({ type, principal }) => ({ type, principal: String(principal) })),
  });

// → { loginSources: [{ id, tenantId, kind, identitySourceId, identitySourceName, priority,
//                      domainSuffixes, enabled, createdAt, updatedAt }] }
export const listLoginSources = () => post("loginSources/list", { tenantId: tenantId() });

// body: { id?, kind: "entra", identitySourceId, domainSuffixes, disabled } → { loginSource }
export const saveLoginSource = (body) => post("loginSources/save", { ...body, tenantId: tenantId() });

// → { status: "deleted" }
export const deleteLoginSource = (id) => post("loginSources/delete", { id });

// → { endpoints: { issuer, discoveryUrl, jwksUrl, authorizationEndpoint, tokenEndpoint,
//                  userinfoEndpoint, endSessionEndpoint, upstreamRedirectUri,
//                  samlMetadataUrl, samlEntityId, samlSsoUrl } }
export const getIdpSettings = () => post("settings", { tenantId: tenantId() });

const statusOf = (err) => err?.response?.status ?? err?.status ?? 0;

// The message to show for a failed call. A 400's message is the backend's own
// and is meant for the administrator, so it is shown as is.
export const idpErrorText = (err, fallback = "Something went wrong. Try again.") => {
  const status = statusOf(err);
  const msg = err?.response?.data?.error;
  if (status === 400 && typeof msg === "string" && msg) return msg;
  if (status === 401) return "Your session has expired. Sign in again.";
  if (status === 403) return "You need the administrator role to manage applications.";
  if (status === 404) return typeof msg === "string" && msg ? `${msg.charAt(0).toUpperCase()}${msg.slice(1)}.` : "Not found.";
  if (status >= 500) return fallback;
  return err?.message && !err?.response ? err.message : fallback;
};

// Sorts a failure: { status, text, logout, forbidden, notFound, invalid, server }.
export const idpFailure = (err, fallback) => {
  const status = statusOf(err);
  return {
    status,
    text: idpErrorText(err, fallback),
    logout: status === 401,
    forbidden: status === 403,
    notFound: status === 404,
    invalid: status === 400,
    server: status >= 500 || status === 0,
  };
};
